import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, ChevronLeft, ChevronRight } from "lucide-react";

const studioData: Record<string, {
  name: string;
  rate: number;
  min: number;
  tagline: string;
  desc: string;
  features: string[];
  addons: string[];
  images: string[];
}> = {
  "studio-a": {
    name: "Studio A",
    rate: 45,
    min: 1,
    tagline: "Versatile Photography Studio",
    desc: "Our versatile Studio A is designed for portrait, headshot, product, and content creation photography. With multiple seamless backdrop options, professional lighting kits, and a comfortable environment, it's the perfect space for photographers of all levels.",
    features: [
      "Multiple seamless paper backdrops (white, gray, black, colors)",
      "Professional strobe & continuous lighting kits",
      "Tether station with calibrated monitor",
      "Private changing area with mirror",
      "High-speed WiFi throughout",
      "Bluetooth speaker system",
      "Comfortable client seating area",
      "Natural light option (window access)",
    ],
    addons: [
      "In-house Shoot Facilitator (+$50/hr)",
      "On-call Editor (+$75/session)",
      "Prop Access (+$25/session)",
    ],
    images: [
      "https://images.unsplash.com/photo-1604514628550-37477afdf4e3?w=800&q=80",
      "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80",
      "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80",
      "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80",
      "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800&q=80",
      "https://images.unsplash.com/photo-1607462109225-6b64ae2dd3cb?w=800&q=80",
    ],
  },
  "studio-b": {
    name: "Studio B",
    rate: 70,
    min: 2,
    tagline: "Premium Production Studio",
    desc: "Studio B is our premium production space featuring a full cyclorama wall, film-grade lighting, and audio dampening — built for cinematography, music videos, editorial shoots, and any project that demands a controlled, high-end environment.",
    features: [
      "Full cyclorama (cyc) wall",
      "Green screen available (+$20/session)",
      "Film-grade continuous lighting rig",
      "4K-ready production setup",
      "Audio dampening for clean sound",
      "Dedicated power circuits (no flicker)",
      "Spacious layout for crew & talent",
      "Ceiling grid for rigging",
    ],
    addons: [
      "In-house Shoot Facilitator (+$50/hr)",
      "On-call Editor (+$75/session)",
      "Prop Access (+$25/session)",
      "Green Screen Setup (+$20/session)",
      "Custom Set Build (Request Quote)",
    ],
    images: [
      "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80",
      "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80",
      "https://images.unsplash.com/photo-1617331140180-e8262094733a?w=800&q=80",
      "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&q=80",
      "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&q=80",
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&q=80",
    ],
  },
};

const StudioDetail = () => {
  const { slug } = useParams();
  const studio = slug ? studioData[slug] : null;
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [mainImage, setMainImage] = useState(0);

  if (!studio) {
    return (
      <div className="pt-32 text-center">
        <h1 className="text-3xl font-display tracking-widest mb-4">STUDIO NOT FOUND</h1>
        <Link to="/studios" className="text-primary hover:underline font-body">View all studios</Link>
      </div>
    );
  }

  const prev = () => setLightbox((v) => (v !== null ? (v - 1 + studio.images.length) % studio.images.length : null));
  const next = () => setLightbox((v) => (v !== null ? (v + 1) % studio.images.length : null));

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/20 z-10" />
        <img src={studio.images[0]} alt={studio.name} className="w-full h-full object-cover" />
        <div className="absolute bottom-0 left-0 right-0 z-20 container mx-auto px-4 pb-10">
          <div className="flex items-center gap-4 mb-3">
            <h1 className="text-4xl md:text-6xl font-display tracking-widest">{studio.name}</h1>
            <span className="bg-primary/10 text-primary px-4 py-1.5 font-mono font-medium text-lg">${studio.rate}/hr</span>
          </div>
          <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">
            {studio.tagline} · Minimum {studio.min} hour{studio.min > 1 ? "s" : ""}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left: Info */}
            <div className="lg:col-span-2 space-y-10">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-display tracking-widest mb-4">ABOUT THIS STUDIO</h2>
                <p className="text-foreground/80 font-body leading-relaxed">{studio.desc}</p>
              </div>

              {/* Photo Gallery Grid */}
              <div>
                <h2 className="text-2xl font-display tracking-widest mb-4">GALLERY</h2>
                <div className="space-y-3">
                  {/* Main image */}
                  <div
                    className="aspect-video overflow-hidden cursor-pointer"
                    onClick={() => setLightbox(mainImage)}
                  >
                    <img
                      src={studio.images[mainImage]}
                      alt={`${studio.name} - ${mainImage + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Thumbnails */}
                  <div className="grid grid-cols-6 gap-2">
                    {studio.images.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setMainImage(i)}
                        className={`aspect-square rounded overflow-hidden border-2 transition-colors ${
                          mainImage === i ? "border-primary" : "border-transparent hover:border-primary/50"
                        }`}
                      >
                        <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-2xl font-display tracking-widest mb-4">FEATURES</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {studio.features.map((f) => (
                    <div key={f} className="flex items-start gap-3 bg-panel border border-border p-4">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-body">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div>
                <h2 className="text-2xl font-display tracking-widest mb-4">AVAILABLE ADD-ONS</h2>
                <div className="space-y-2">
                  {studio.addons.map((a) => (
                    <div key={a} className="flex items-center gap-3 bg-panel border border-border p-4">
                      <div className="w-2 h-2 bg-primary" />
                      <span className="text-sm font-body">{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Mini Booking Widget */}
            <div className="lg:col-span-1">
              <div className="bg-panel border border-border p-6 lg:sticky lg:top-24">
                <h3 className="font-display tracking-widest text-xl mb-2 text-center">{studio.name}</h3>
                <p className="text-center text-3xl font-display text-primary mb-1">${studio.rate}<span className="text-base text-muted-foreground">/hr</span></p>
                <p className="text-center text-xs text-muted-foreground font-mono mb-6">Min {studio.min} hour{studio.min > 1 ? "s" : ""} · 50% deposit</p>

                <div className="space-y-3 mb-6">
                  <div className="bg-background border border-border p-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-2">Next Available</p>
                    {["Tomorrow", "Wed, Apr 9", "Thu, Apr 10", "Fri, Apr 11", "Sat, Apr 12"].map((d) => (
                      <div key={d} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                        <span className="text-sm font-body">{d}</span>
                        <span className="text-xs text-success font-mono">Open</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link to={`/book?studio=${slug}`}>
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold text-base py-3">
                    Book {studio.name}
                  </Button>
                </Link>

                <p className="text-xs text-muted-foreground text-center mt-4 font-body">
                  You won't be charged until you confirm
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-foreground hover:text-primary">
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-foreground hover:text-primary">
            <ChevronRight className="w-8 h-8" />
          </button>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 text-foreground hover:text-primary">
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={studio.images[lightbox]} alt={`${studio.name} ${lightbox + 1}`} className="max-w-full max-h-[80vh] object-contain" />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudioDetail;
